import { useCreateEnvironmentAccessToken, useEnvironment } from "@/queries/environment-queries";
import { createPromiseClient, type PromiseClient } from "@connectrpc/connect";
import { createConnectTransport } from "@connectrpc/connect-web";
import { useMemo } from "react";
import { EnvironmentOpsService } from "gitpod-next-api/supervisor/v1/environmentopts_connect";

export type EnvironmentOpsServiceClient = PromiseClient<typeof EnvironmentOpsService>;

export const useEnvironmentOpsService = (environmentId: string): EnvironmentOpsServiceClient | null => {
    const { data: environment } = useEnvironment(environmentId);
    const { data: environmentAccessToken } = useCreateEnvironmentAccessToken(environment?.id);
    const transport = useMemo(() => {
        const opsUrlString = environment?.status?.environmentUrls?.ops;
        const token = environmentAccessToken;
        if (!opsUrlString || !token) {
            return null;
        }

        // createConnectTransport expects a base URL to the env ops service
        // and the expected path under which the env ops service is served is appended automatically
        // however, the ops url also contains that path, so we need to remove it
        // otherwise, the path will be duplicated
        const supervisorUrl = new URL(opsUrlString);
        supervisorUrl.pathname = "";

        return createConnectTransport({
            baseUrl: supervisorUrl.toString(),
            useBinaryFormat: false,
            interceptors: [
                (next) => async (req) => {
                    req.header.set("Authorization", `Bearer ${token}`);
                    return await next(req);
                },
            ],
            defaultTimeoutMs: 30 * 1000,
        });
    }, [environmentAccessToken, environment]);
    return useMemo(() => (transport ? createPromiseClient(EnvironmentOpsService, transport) : null), [transport]);
};
